import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import InfiniteScroll from '@/components/ui/infinite-scroll';
import { formatHoursToTimeString, minutesToHours } from '@/helpers';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { formatWallClockWeekdayDate } from '@/utils/date-formatters/date-formatters';

import { groupTracksByDate } from '../../helpers/group-tracks-by-date.helper';
import { useTimeTracks } from '../../hooks/use-time-tracks';
import { useWorklogEditing } from '../../hooks/use-worklog-editing';
import { ProjectTimeTrackingSummary, TimeTrack, ViewMode } from '../../types/time-tracking';
import { TimeTrackEntry } from '../time-track-entry';

interface ProjectWeekEntriesProps {
  project: ProjectTimeTrackingSummary;
  weekStart: Date;
  /**
   * When set (`YYYY-MM-DD`), only that day's group is expanded and the others
   * are collapsed (day-cell click). `null` means the row was opened via the
   * project name → all days are expanded (OPS-1565).
   */
  focusedDay: string | null;
  /**
   * Booked hours for THIS project per UTC calendar day (YYYY-MM-DD). A day is
   * present only when the project has a booking for it; absent days render
   * tracked hours only (OPS-1520).
   */
  bookedHoursByDate: Record<string, number>;
}

interface DayGroupProps {
  isEditVisible: boolean;
  isDeleteVisible: boolean;
  date: string;
  tracks: TimeTrack[];
  /** Booked hours for this project on this day, or undefined when not booked. */
  bookedHoursPerDay: number | undefined;
  /** Desired open state driven by the focused-day selection (OPS-1565). */
  desiredOpen: boolean;
  onEdit: (e: React.MouseEvent<HTMLButtonElement>, track: TimeTrack) => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement>, track: TimeTrack) => void;
}

const DAYGROUP_ANIMATION_MS = 200;
const DAYGROUP_ANIMATION_TIMEOUT_MS = 260;

function DayGroup({
  isEditVisible,
  isDeleteVisible,
  date,
  tracks,
  bookedHoursPerDay,
  desiredOpen,
  onEdit,
  onDelete,
}: DayGroupProps) {
  const [open, setOpen] = useState(desiredOpen);
  const { t } = useTranslation('authenticated.timeTracking');

  // Re-sync to the focused-day selection when it changes (e.g. user clicks a
  // different day cell), while still allowing manual toggle within the group.
  useEffect(() => {
    setOpen(desiredOpen);
  }, [desiredOpen]);

  const dailyTrackedHours = minutesToHours(
    tracks.reduce((sum, track) => sum + track.trackedMinutes, 0)
  );

  const hasBookedHours = bookedHoursPerDay !== undefined;
  const dailySummary = hasBookedHours
    ? t('project.dailySummary', {
        tracked: formatHoursToTimeString(dailyTrackedHours),
        booked: formatHoursToTimeString(bookedHoursPerDay),
      })
    : t('project.trackedLabel', {
        tracked: formatHoursToTimeString(dailyTrackedHours),
      });

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="flex w-full items-center gap-2 rounded-md p-3 hover:bg-muted/50"
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${!open ? '-rotate-90' : ''}`} />
        <span className="text-sm font-medium">{formatWallClockWeekdayDate(date)}</span>
        <span className="ml-auto text-sm text-muted-foreground">{dailySummary}</span>
      </button>
      <DayGroupCollapse open={open}>
        <div className="flex flex-col gap-1.5 pb-1 pl-8 pt-1">
          {tracks.map(track => (
            <div
              key={track.id}
              className={cn(
                'overflow-hidden rounded-md border-l-[3px] bg-muted/40 shadow-md transition-colors hover:bg-muted/70',
                track.isApproved ? 'border-green-500' : 'border-border'
              )}
            >
              <TimeTrackEntry
                track={track}
                onEdit={e => onEdit(e, track)}
                onDelete={e => onDelete(e, track)}
                isEditVisible={isEditVisible}
                isDeleteVisible={isDeleteVisible}
                hideStatusIndicator
                // The DayGroup wrapper card is already on `bg-muted/40`, so
                // the default `bg-muted` chips disappear into it. Bump the
                // chip / hover tone one notch darker for legibility.
                accentBgClassName="bg-muted-foreground/10"
                accentBgGroupHoverClassName="group-hover/entry:bg-muted-foreground/10"
                accentBgHoverClassName="hover:bg-muted-foreground/15"
              />
            </div>
          ))}
        </div>
      </DayGroupCollapse>
    </div>
  );
}

/**
 * Self-contained height-based collapse for a DayGroup's content. We do NOT
 * use Radix `Collapsible` here because its CSS keyframe animation
 * (`var(--radix-collapsible-content-height) → 0`) was firing alongside the
 * outer ProjectExpandedRow's transition and producing a visible "double
 * collapse" — the inner keyframe ran once and the outer wrapper's `auto`
 * reflow snapped at a slightly different cadence.
 *
 * Driving the height ourselves with the same `useLayoutEffect` + double-rAF
 * pattern as the outer wrapper gives both layers identical 200 ms timing
 * curves, so they shrink in lockstep and finish in a single visible motion.
 */
function DayGroupCollapse({ open, children }: { open: boolean; children: React.ReactNode }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>(open ? 'auto' : 0);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    if (open) {
      const target = content.scrollHeight;
      setHeight(target);
      const release = () => setHeight('auto');
      const onEnd = (event: TransitionEvent) => {
        if (event.target !== wrapper || event.propertyName !== 'height') return;
        release();
      };
      wrapper.addEventListener('transitionend', onEnd);
      const fallback = window.setTimeout(release, DAYGROUP_ANIMATION_TIMEOUT_MS);
      return () => {
        wrapper.removeEventListener('transitionend', onEnd);
        window.clearTimeout(fallback);
      };
    }

    // Close: pin to the current measured numeric height, then on the next
    // frame flip to 0 so the transition has a numeric starting point. Double
    // rAF guarantees the pinned frame is painted before the 0 update lands.
    const current = content.scrollHeight;
    setHeight(current);
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setHeight(0));
    });
    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      style={{
        height: height === 'auto' ? 'auto' : `${height}px`,
        overflow: 'hidden',
        transition: `height ${DAYGROUP_ANIMATION_MS}ms ease-out`,
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

export function ProjectWeekEntries({
  project,
  weekStart,
  focusedDay,
  bookedHoursByDate,
}: ProjectWeekEntriesProps) {
  const { t } = useTranslation('authenticated.timeTracking');

  const { openEdit, openDelete, modalsElement } = useWorklogEditing({
    project: project.project,
  });

  const {
    data: timeTracksData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useTimeTracks({
    date: weekStart,
    viewMode: ViewMode.Week,
    projectId: project.project.projectId,
    enabled: true,
  });

  const timeTracks = timeTracksData?.items ?? [];
  const tracksByDate = groupTracksByDate(timeTracks);

  const { hasPermission } = usePermissions();
  const canEditWorklog = hasPermission(OpsiumPolicy.WorklogsEdit);
  const canDeleteWorklog = hasPermission(OpsiumPolicy.WorklogsDelete);

  return (
    <>
      {modalsElement}

      {isLoading && (
        <div className="flex items-center justify-center p-6">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}

      {!isLoading && timeTracks.length === 0 && (
        <div className="p-6 text-center text-sm text-muted-foreground">
          {t('project.noEntries')}
        </div>
      )}

      {!isLoading && timeTracks.length > 0 && (
        <InfiniteScroll isLoading={isFetchingNextPage} hasMore={!!hasNextPage} next={fetchNextPage}>
          {Object.entries(tracksByDate)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, tracks]) => (
              <DayGroup
                key={date}
                date={date}
                tracks={tracks}
                bookedHoursPerDay={bookedHoursByDate[date.slice(0, 10)]}
                // No focused day → all days open (name-expand). Otherwise only
                // the focused day's group is open (OPS-1565).
                desiredOpen={focusedDay === null || focusedDay === date.slice(0, 10)}
                isEditVisible={canEditWorklog}
                isDeleteVisible={canDeleteWorklog}
                onEdit={(e, track) => {
                  e.preventDefault();
                  openEdit(track);
                }}
                onDelete={(e, track) => {
                  e.preventDefault();
                  openDelete(track);
                }}
              />
            ))}
        </InfiniteScroll>
      )}

      {isFetchingNextPage && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}
    </>
  );
}
